import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Global } from '@emotion/react';
import { MediaQueryProvider } from 'react-media-query-hoc';
import { SearchAutocomplete } from '..';
import { UltimateGuide, CallToActions } from './components';
import * as styles from './landing-page.style';
import { trackLandingPage } from '../../tracking';
import { getPublicUrl } from '../../utils';
import {
  FAA_LANDING_PAGE_MODULE,
  getVisibleLandingPageModules,
} from '../../tracking/landing-page';
import type { FaaLandingPageModule } from '../../tracking/landing-page';

interface LandingPageProps {
  skipLinkContentId: string;
  mixpanelHasInitialized?: boolean;
  showCallToActions?: boolean;
  showUltimateGuide?: boolean;
  showSuburbSnapshot?: boolean;
}

const FindAnAgent = ({
  skipLinkContentId,
  mixpanelHasInitialized = false,
  showCallToActions = true,
  showUltimateGuide = true,
  showSuburbSnapshot = false,
}: LandingPageProps): JSX.Element => {
  const hasTrackedView = useRef(false);
  const [loadedModules, setLoadedModules] = useState<FaaLandingPageModule[]>(
    [],
  );

  const visibleModules = useMemo(
    () =>
      getVisibleLandingPageModules({
        hasContentCarouselGuides: showUltimateGuide,
        hasCtaDriverGetAppraisal: showCallToActions,
        hasCtaDriverTrackHome: showCallToActions,
        hasCtaDriverRecentlySold: showCallToActions,
        hasSuburbSnapshot: showSuburbSnapshot,
      }),
    [showUltimateGuide, showCallToActions, showSuburbSnapshot],
  );

  const onModuleRendered = useCallback((module: FaaLandingPageModule): void => {
    setLoadedModules((previousModules) => {
      if (previousModules.includes(module)) {
        return previousModules;
      }

      return [...previousModules, module];
    });
  }, []);

  useEffect(() => {
    if (showSuburbSnapshot) {
      onModuleRendered(FAA_LANDING_PAGE_MODULE.SUBURB_SNAPSHOT);
    }
  }, [showSuburbSnapshot, onModuleRendered]);

  useEffect(() => {
    const allVisibleModulesLoaded = visibleModules.every((module) =>
      loadedModules.includes(module),
    );

    if (
      mixpanelHasInitialized &&
      allVisibleModulesLoaded &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      trackLandingPage.trackViewLandingPage(visibleModules);
    }
  }, [mixpanelHasInitialized, visibleModules, loadedModules]);

  const bannerImage = getPublicUrl('/background.png');

  return (
    <MediaQueryProvider values={{ width: 300, type: 'screen' }}>
      <div css={styles.container}>
        <Global styles={styles.globalStyle} />

        <div css={styles.bannerContainer(bannerImage, bannerImage)}>
          <div css={styles.landing}>
            <div css={styles.wrapper}>
              <div css={styles.content} id={skipLinkContentId}>
                <h1 css={styles.title}>
                  <span>Find your local</span>
                  <span> Real Estate Agent</span>
                </h1>
                <p css={styles.subtitle}>Choose from over 45,000 nationwide</p>
                <div css={styles.search}>
                  <SearchAutocomplete
                    recentSearches
                    icons
                    isLocationSearchByAgent
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div css={styles.page}>
          {showCallToActions && (
            <div css={styles.wrapper}>
              <CallToActions onModuleRendered={onModuleRendered} />
            </div>
          )}
          {showUltimateGuide && (
            <div css={styles.wrapper}>
              <UltimateGuide onModuleRendered={onModuleRendered} />
            </div>
          )}
          {showSuburbSnapshot && (
            <div css={styles.wrapper} data-testid="suburb-snapshot-module" />
          )}
        </div>
      </div>
    </MediaQueryProvider>
  );
};

export default FindAnAgent;
